/* ==========================================================================
   PDP Catalog Rules / MOQ  (assets/pdp-moq.js)
   --------------------------------------------------------------------------
   Client-side half of snippets/pdp-moq.liquid.

   Source of truth is the NATIVE Shopify catalog data:
     variant.quantity_rule          { min, max, increment }
     variant.quantity_price_breaks  [ { minimum_quantity, price } ]

   Those are evaluated server-side per request against the logged-in customer's
   company location / catalog. A guest gets the store defaults (min 1,
   increment 1) and no price breaks - that is Shopify's behaviour, not a bug.

   Because the Ajax product endpoint (/products/{handle}.js) does NOT return
   quantity_rule or quantity_price_breaks, snippets/pdp-moq.liquid pre-renders
   every variant's rules into <script id="pdp-variant-rules-json">. This module
   reads that map on variant switch instead of making a request.

   Data contract:
     #pdp-catalog-rules[data-pdp-moq]   resolved rules for the initial variant
       data-min / data-max / data-step  + data-*-source markers
       ALSO the hidden `name="quantity"` carrier that the product form submits -
       the customer-facing quantity control is the tier bar in
       snippets/bulk-pricing-calculator.liquid, so a visible
       [data-pdp-moq-qty] input is OPTIONAL and both are supported.
     #pdp-variant-rules-json            { rules: {id: {...}}, price_breaks: {id: [...]} }
     #pdp-price-breaks-json             breaks for the initial variant (optional)
     [data-pdp-moq-qty]                 optional visible quantity input
     [data-pdp-moq-hint]                optional hint element
     [data-pdp-moq-change]              optional +/- buttons

   Public API:
     window.PDPMoq.rules                      -> { moq, step, max, inventoryCap, sources }
     window.PDPMoq.normalize(n)               -> quantity snapped to a valid value
     window.PDPMoq.isValid(n)
     window.PDPMoq.bind(input)
     window.PDPMoq.setQuantity(n) / getQuantity()
     window.PDPMoq.syncCatalogRulesToVariant(variant)
     window.PDPMoq.getPriceBreaks(variantId)  -> [{ minimum_quantity, price }]

   Events:
     listens  'pdp:variant-change', 'variant-radios:change', 'bulk:quantity-change'
     emits    'pdp:quantity-change', 'pdp:moq-ready'
   ========================================================================== */

(function () {
  'use strict';

  var PDPMoq = {
    version: '2.0.0',
    rules: { moq: 1, step: 1, max: null, inventoryCap: null, sources: {} },
    variantRules: null,
    variantPriceBreaks: null,
    root: null,
    _lastEmitted: null
  };

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /** Effective upper bound = min(configured max, inventory cap). */
  function effectiveMax() {
    var max = PDPMoq.rules.max;
    var cap = PDPMoq.rules.inventoryCap;
    if (max && cap) return Math.min(max, cap);
    return max || cap || null;
  }

  /**
   * Snap a request to the closest valid quantity:
   *   below MOQ            -> MOQ
   *   MOQ .. MOQ+step/2    -> MOQ   (MOQ 10, step 10: 14 -> 10)
   *   otherwise            -> nearest multiple of step, counted from MOQ
   *   above max / stock    -> max
   */
  PDPMoq.normalize = function (requested) {
    var moq = PDPMoq.rules.moq || 1;
    var step = PDPMoq.rules.step || 1;
    var max = effectiveMax();

    var qty = toInt(requested, moq);
    if (qty < moq) return moq;

    if (step > 1) {
      qty = moq + Math.round((qty - moq) / step) * step;
      if (qty < moq) qty = moq;
    }
    if (max && qty > max) qty = max;
    return qty;
  };

  PDPMoq.isValid = function (qty) {
    var moq = PDPMoq.rules.moq || 1;
    var step = PDPMoq.rules.step || 1;
    var max = effectiveMax();

    qty = toInt(qty, NaN);
    if (isNaN(qty) || qty < moq) return false;
    if (step > 1 && (qty - moq) % step !== 0) return false;
    if (max && qty > max) return false;
    return true;
  };

  /** Human-readable summary built from the enforced rules. */
  PDPMoq.describe = function () {
    var parts = [];
    var max = effectiveMax();
    if ((PDPMoq.rules.moq || 1) > 1) parts.push('Minimum order ' + PDPMoq.rules.moq);
    if ((PDPMoq.rules.step || 1) > 1) parts.push('in multiples of ' + PDPMoq.rules.step);
    if (max) parts.push('maximum ' + max);
    return parts.join(' \u00b7 ');
  };

  function emit(quantity) {
    if (quantity === PDPMoq._lastEmitted) return;
    PDPMoq._lastEmitted = quantity;
    document.dispatchEvent(new CustomEvent('pdp:quantity-change', {
      detail: { quantity: quantity, rules: PDPMoq.rules }
    }));
  }

  function updateHint() {
    var hint = document.querySelector('[data-pdp-moq-hint]');
    if (!hint) return;
    var text = PDPMoq.describe();
    hint.textContent = text;
    hint.hidden = text === '';
  }

  /**
   * The quantity control.
   *
   * There may be no visible input: the customer-facing control on the MC PDP is
   * the tier bar in snippets/bulk-pricing-calculator.liquid, and snippets/pdp-moq.liquid
   * then renders only a hidden `name="quantity"` carrier. Both are supported -
   * a visible input wins, otherwise the hidden submitted field is driven directly.
   */
  function qtyControl() {
    return document.querySelector('[data-pdp-moq-qty]') || document.querySelector('[data-pdp-moq]');
  }

  /** Push the current rules onto the control element itself (when it can hold them). */
  function applyRulesToControl() {
    var control = qtyControl();
    if (!control) return;
    // A hidden input has no min/max/step semantics, so only set them on a real
    // number field; the hidden carrier just holds the value.
    if (control.type === 'hidden') return;
    control.min = PDPMoq.rules.moq;
    control.step = PDPMoq.rules.step;
    var max = effectiveMax();
    if (max) control.max = max; else control.removeAttribute('max');
  }

  /** Keep the control, the hint and any consumers in agreement. */
  function applyInput(control, mirror, requested, source) {
    var qty = PDPMoq.normalize(requested);

    if (mirror) {
      var target = control || qtyControl();
      if (target) target.value = qty;
    }

    updateHint();
    emit(qty);
    return qty;
  }

  PDPMoq.setQuantity = function (value, source) {
    return applyInput(qtyControl(), true, value, source);
  };

  PDPMoq.getQuantity = function () {
    var control = qtyControl();
    return control ? PDPMoq.normalize(control.value) : (PDPMoq.rules.moq || 1);
  };

  /**
   * Normalise on change/blur, not on every keystroke, so typing "15" into a
   * field with MOQ 10 step 10 is not fought mid-entry.
   * Hidden carriers have nothing to type into, so this is a no-op for them.
   */
  PDPMoq.bind = function (control) {
    if (!control || control.type === 'hidden' || control.dataset.pdpMoqBound === 'true') return;
    control.dataset.pdpMoqBound = 'true';

    control.addEventListener('change', function () { applyInput(control, true, control.value); });
    control.addEventListener('blur', function () { applyInput(control, true, control.value); });
    control.addEventListener('input', function () {
      var value = toInt(control.value, null);
      if (value === null) return;
      var moq = PDPMoq.rules.moq || 1;
      var max = effectiveMax();
      if (value < moq || (max && value > max)) applyInput(control, true, control.value);
    });
  };

  /* ------------------------------------------------------------------ *
   * Native catalog properties
   * ------------------------------------------------------------------ */

  /** Read the pre-rendered per-variant map. */
  function loadVariantMap() {
    var el = document.getElementById('pdp-variant-rules-json');
    if (!el) return false;
    try {
      var parsed = JSON.parse(el.textContent);
      PDPMoq.variantRules = parsed.rules || {};
      PDPMoq.variantPriceBreaks = parsed.price_breaks || {};
      return true;
    } catch (error) {
      console.warn('[PDPMoq] could not parse #pdp-variant-rules-json', error);
      return false;
    }
  }

  /** Native price breaks for a variant, from the pre-rendered map. */
  PDPMoq.getPriceBreaks = function (variantId) {
    if (PDPMoq.variantPriceBreaks && PDPMoq.variantPriceBreaks[String(variantId)]) {
      return PDPMoq.variantPriceBreaks[String(variantId)];
    }
    return [];
  };

  /**
   * Apply a variant's native catalog rules.
   *
   * Consumes the `quantity_rule` object exactly as Shopify renders it:
   *   { min, max, increment }  - and pre-rendered per variant when the variant
   *   object itself is not available client-side.
   */
  function syncCatalogRulesToVariant(variant) {
    if (!variant) return;

    var variantId = variant.id;
    var mapEntry = variantId !== undefined && PDPMoq.variantRules
      ? PDPMoq.variantRules[String(variantId)]
      : null;

    // Prefer live properties when present, fall back to the pre-rendered map.
    var rule = variant.quantity_rule || mapEntry || {};
    var min = toInt(rule.min, 1) || 1;
    var max = toInt(rule.max, null);
    var step = toInt(rule.increment, 1) || 1;

    PDPMoq.rules.moq = min;
    PDPMoq.rules.step = step;
    PDPMoq.rules.max = max;
    PDPMoq.rules.sources = {
      min: variant.quantity_rule ? 'variant_catalog' : (mapEntry ? 'variant_map' : 'rendered_default'),
      step: variant.quantity_rule ? 'variant_catalog' : (mapEntry ? 'variant_map' : 'rendered_default'),
      max: variant.quantity_rule ? 'variant_catalog' : (mapEntry ? 'variant_map' : 'rendered_default')
    };

    // Inventory cap: tracked stock with backorders disallowed.
    var inventoryQty = variant.inventory_quantity;
    var inventoryPolicy = variant.inventory_policy;
    var inventoryManagement = variant.inventory_management;
    if (mapEntry) {
      if (inventoryQty === undefined) inventoryQty = mapEntry.inventory_quantity;
      if (inventoryPolicy === undefined) inventoryPolicy = mapEntry.inventory_policy;
      if (inventoryManagement === undefined) inventoryManagement = mapEntry.inventory_management;
    }
    PDPMoq.rules.inventoryCap = (inventoryManagement && inventoryPolicy !== 'continue' &&
      typeof inventoryQty === 'number' && inventoryQty > 0) ? inventoryQty : null;

    var control = qtyControl();
    if (control && control.type !== 'hidden') {
      control.min = min;
      control.step = step;
      if (max) {
        control.max = max;
      } else {
        control.removeAttribute('max');
      }
    }

    // Auto-adjust if the current quantity violates the new variant minimum.
    var current = control ? toInt(control.value, 0) : 0;
    if (current < min && control) {
      control.value = min;
    }

    applyInput(control, true, control ? control.value : min, 'variant');
  }

  // Exposed under the documented name so it can be called directly.
  PDPMoq.syncCatalogRulesToVariant = syncCatalogRulesToVariant;

  /* ------------------------------------------------------------------ *
   * Rules loading + init
   * ------------------------------------------------------------------ */

  function loadRenderedRules() {
    PDPMoq.root = document.querySelector('[data-pdp-moq]');
    if (!PDPMoq.root) {
      console.warn('[PDPMoq] [data-pdp-moq] not found - is snippets/pdp-moq.liquid rendered?');
      return false;
    }
    var el = PDPMoq.root;
    PDPMoq.rules = {
      moq: toInt(el.dataset.min, 1) || 1,
      step: toInt(el.dataset.step, 1) || 1,
      max: toInt(el.dataset.max, null),
      inventoryCap: toInt(el.dataset.inventoryCap, null),
      sources: {
        min: el.dataset.minSource || 'unknown',
        step: el.dataset.stepSource || 'unknown',
        max: el.dataset.maxSource || 'unknown'
      }
    };
    return true;
  }

  function bindButtons() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pdp-moq-change]'), function (button) {
      if (button.dataset.pdpMoqBound === 'true') return;
      button.dataset.pdpMoqBound = 'true';
      button.addEventListener('click', function (event) {
        event.preventDefault();
        var direction = toInt(button.getAttribute('data-pdp-moq-change'), 0);
        var current = PDPMoq.getQuantity();
        PDPMoq.setQuantity(current + direction * (PDPMoq.rules.step || 1));
      });
    });
  }

  function init() {
    if (!loadRenderedRules()) return;
    loadVariantMap();

    var control = qtyControl();
    applyRulesToControl();
    if (control) PDPMoq.bind(control);
    bindButtons();
    updateHint();
    applyInput(control, false, control ? control.value : PDPMoq.rules.moq, 'init');

    // Variant switching -> re-resolve native catalog rules from the map.
    document.addEventListener('pdp:variant-change', function (event) {
      if (event.detail && event.detail.variant) syncCatalogRulesToVariant(event.detail.variant);
    });
    var picker = document.querySelector('variant-radios');
    if (picker) {
      picker.addEventListener('variant-radios:change', function (event) {
        if (event.detail && event.detail.variant) syncCatalogRulesToVariant(event.detail.variant);
      });
    }

    // The bulk calculator picked a tier: adopt it as the order quantity.
    document.addEventListener('bulk:quantity-change', function (event) {
      if (event.detail && event.detail.quantity) PDPMoq.setQuantity(event.detail.quantity, 'bulk');
    });

    document.dispatchEvent(new CustomEvent('pdp:moq-ready', { detail: { rules: PDPMoq.rules } }));
    console.log('[PDPMoq] rules', PDPMoq.rules, '| sources', PDPMoq.rules.sources);
  }

  PDPMoq.init = init;
  window.PDPMoq = PDPMoq;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
